import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useApiCall } from "@framework/react-hooks";

import type { IChartData, ISignalDataResponse } from "../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisDataMark, TSignalDisplayUnit } from "../types";
import {
  applyFrequencyProfileToTrendSeries,
  createFrequencyProfilePointKey,
  getUniqueFrequencyProfileMeasureRefs,
} from "../utils/frequency-profile";
import {
  buildSignalDataCacheKey,
  fetchMultiAnalysisSignalDataInChunks,
  type IMultiAnalysisSignalMeasureRef,
} from "../utils/signal-data";

interface IUseMultiAnalysisFrequencyProfileSeriesArgs {
  enabled: boolean;
  nativeX: number | null;
  dataMarks: readonly IMultiAnalysisDataMark[];
  baseSeries: readonly IChartData[];
  signalDisplayUnits: TSignalDisplayUnit;
}

export const useMultiAnalysisFrequencyProfileSeries = ({
  enabled,
  nativeX,
  dataMarks,
  baseSeries,
  signalDisplayUnits,
}: IUseMultiAnalysisFrequencyProfileSeriesArgs): { series: IChartData[]; isLoading: boolean } => {
  const { formatMessage } = useIntl();
  const [signals, setSignals] = useState<ISignalDataResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const relevantDataMarks = useMemo(() => {
    if (!enabled || nativeX == null) {
      return [];
    }

    const trendPointKeys = new Set<string>();

    baseSeries.forEach(item => {
      item.items.forEach(point => {
        trendPointKeys.add(createFrequencyProfilePointKey(item.idMeasure, item.idPoint, point.date));
      });
    });

    return dataMarks.filter(mark =>
      trendPointKeys.has(createFrequencyProfilePointKey(mark.idMeasure, mark.idPoint, mark.date)),
    );
  }, [baseSeries, dataMarks, enabled, nativeX]);
  const measureRefs = useMemo(() => getUniqueFrequencyProfileMeasureRefs(relevantDataMarks), [relevantDataMarks]);
  const measureRefsKey = buildSignalDataCacheKey(measureRefs);
  const canApply = enabled && nativeX != null && measureRefs.length > 0;

  const { fn: loadSignals } = useApiCall(
    async (api, refs: readonly IMultiAnalysisSignalMeasureRef[]): Promise<ISignalDataResponse[]> =>
      fetchMultiAnalysisSignalDataInChunks(api, refs),
    { success: false, error: false },
  );
  const loadSignalsRef = useRef(loadSignals);

  useEffect(() => {
    loadSignalsRef.current = loadSignals;
  }, [loadSignals]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!canApply) {
      setSignals([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void loadSignalsRef
      .current(undefined, measureRefs)
      .then(nextSignals => {
        if (requestIdRef.current === requestId) {
          setSignals(nextSignals ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setSignals([]);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [canApply, measureRefsKey]);

  const series = useMemo((): IChartData[] => {
    if (!canApply || nativeX == null || isLoading) {
      return [...baseSeries];
    }

    return applyFrequencyProfileToTrendSeries(
      baseSeries,
      signals,
      relevantDataMarks,
      nativeX,
      signalDisplayUnits,
      unitCode => formatMessage({ id: `enums.units.${unitCode}` }),
    );
  }, [baseSeries, canApply, formatMessage, isLoading, nativeX, relevantDataMarks, signalDisplayUnits, signals]);

  return {
    series,
    isLoading: canApply && isLoading,
  };
};
